import supabase from '../supabase/supabase';

/*
 * =========================================================
 * PROFITYO QUICKBOOKS SERVICE
 * =========================================================
 *
 * Edge Functions:
 * - quickbooks-accounts
 * - quickbooks-import
 *
 * quickbooks-import actions:
 * - preview
 * - stage
 *
 * Transactions are staged for review.
 * No automatic posting to the live ledger.
 * =========================================================
 */

const ACCOUNT_PAGE_SIZE = 1000;

const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/*
 * =========================================================
 * VALIDATION
 * =========================================================
 */

const validateOrganizationId = (organizationId) => {
  const id = String(organizationId ?? '').trim();

  if (!UUID_REGEX.test(id)) {
    throw new Error(
      'A valid client organization ID is required.'
    );
  }

  return id;
};

const validateDate = (value, fieldName) => {
  const date = String(value ?? '').trim();

  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
    throw new Error(
      `${fieldName} must use YYYY-MM-DD format.`
    );
  }

  const parsed = new Date(`${date}T00:00:00.000Z`);

  if (
    Number.isNaN(parsed.getTime()) ||
    parsed.toISOString().slice(0, 10) !== date
  ) {
    throw new Error(`Invalid ${fieldName}.`);
  }

  return date;
};

const validateDateRange = (startDate, endDate) => {
  const start = validateDate(startDate, 'Start date');
  const end = validateDate(endDate, 'End date');

  if (start > end) {
    throw new Error(
      'Start date cannot be after end date.'
    );
  }

  return {
    startDate: start,
    endDate: end
  };
};

/*
 * =========================================================
 * AUTHENTICATION
 * =========================================================
 */

const requireSession = async () => {
  const { data, error } =
    await supabase.auth.getSession();

  if (error) {
    throw new Error(
      'Unable to verify your login session.'
    );
  }

  const session = data?.session;

  if (!session?.access_token) {
    throw new Error(
      'Your session has expired. Please sign in again.'
    );
  }

  return session;
};

/*
 * =========================================================
 * ERROR HANDLING
 * =========================================================
 */

const getFunctionErrorMessage = async (
  error,
  fallback
) => {
  const context = error?.context;

  if (context && typeof context.clone === 'function') {
    try {
      const response = context.clone();
      const body = await response.json();

      const message =
        body?.error ||
        body?.message ||
        body?.details;

      if (typeof message === 'string' && message.trim()) {
        return message;
      }
    } catch {
      // Ignore invalid JSON responses.
    }
  }

  return error?.message || fallback;
};

const invokeQuickBooksFunction = async (
  functionName,
  body
) => {
  const session = await requireSession();

  const { data, error } =
    await supabase.functions.invoke(
      functionName,
      {
        headers: {
          Authorization: `Bearer ${session.access_token}`
        },
        body
      }
    );

  if (error) {
    const message = await getFunctionErrorMessage(
      error,
      `QuickBooks request failed: ${functionName}.`
    );

    console.error(
      `[QuickBooks] ${functionName} failed:`,
      message
    );

    throw new Error(message);
  }

  if (!data || data.success !== true) {
    throw new Error(
      data?.error ||
      data?.message ||
      'QuickBooks returned an unsuccessful response.'
    );
  }

  return data;
};

/*
 * =========================================================
 * SOURCE ACCOUNTS
 * =========================================================
 */

export async function getQuickBooksSourceAccounts({
  organizationId
}) {
  const orgId = validateOrganizationId(organizationId);
  const data = await invokeQuickBooksFunction(
    'quickbooks-import',
    {
      action: 'source-accounts',
      organizationId: orgId
    }
  );

  if (
    data.action !== 'source-accounts' ||
    !Array.isArray(data.accounts)
  ) {
    throw new Error(
      'QuickBooks returned an invalid source account response.'
    );
  }

  const accounts = data.accounts
    .filter((account) => {
      const type = String(account?.accountType || '')
        .toLowerCase()
        .replace(/\s+/g, '');
      return type === 'bank' || type === 'creditcard';
    })
    .map((account) => ({
      ...account,
      id: String(account.id ?? ''),
      name: String(account.name ?? ''),
      fullyQualifiedName: String(
        account.fullyQualifiedName ?? account.name ?? ''
      ),
      accountType: String(account.accountType ?? ''),
      accountSubType: String(account.accountSubType ?? '')
    }))
    .filter((account) => account.id && account.name);

  return {
    ...data,
    accounts
  };
}

/*
 * =========================================================
 * SOURCE TRANSACTIONS
 * =========================================================
 */

export async function getQuickBooksSourceTransactions({
  organizationId,
  connectionId,
  sourceAccountId,
  startDate,
  endDate
}) {
  const orgId = validateOrganizationId(organizationId);
  const range = validateDateRange(startDate, endDate);
  const normalizedConnectionId = String(connectionId ?? '').trim();
  const normalizedSourceAccountId = String(sourceAccountId ?? '').trim();

  if (!normalizedConnectionId || !normalizedSourceAccountId) {
    throw new Error(
      'A QuickBooks connection and source account are required.'
    );
  }

  const data = await invokeQuickBooksFunction(
    'quickbooks-import',
    {
      action: 'source-transactions',
      organizationId: orgId,
      connectionId: normalizedConnectionId,
      sourceAccountId: normalizedSourceAccountId,
      ...range
    }
  );

  if (
    data.action !== 'source-transactions' ||
    !Array.isArray(data.transactions)
  ) {
    throw new Error(
      'QuickBooks returned an invalid source transaction response.'
    );
  }

  return data;
}

/*
 * =========================================================
 * SAVE QUICKBOOKS ACCOUNT MAPPINGS
 * =========================================================
 */

export async function saveQuickBooksMappings({
  organizationId,
  connectionId,
  mappings
}) {
  const orgId = validateOrganizationId(organizationId);
  const normalizedConnectionId = String(connectionId ?? '').trim();

  if (!normalizedConnectionId) {
    throw new Error('A QuickBooks connection is required.');
  }

  if (!Array.isArray(mappings) || mappings.length === 0) {
    throw new Error('At least one account mapping is required.');
  }

  const result = await invokeQuickBooksFunction(
    'quickbooks-import',
    {
      action: 'save-mappings',
      organizationId: orgId,
      connectionId: normalizedConnectionId,
      mappings
    }
  );

  if (result.action !== 'save-mappings') {
    throw new Error(
      'QuickBooks did not confirm the mapping update.'
    );
  }

  return result;
}

/*
 * =========================================================
 * PREVIEW QUICKBOOKS TRANSACTIONS
 * =========================================================
 *
 * Calls quickbooks-import with action: preview
 *
 * Expected response:
 * {
 *   success: true,
 *   transactions: [...]
 * }
 * =========================================================
 */

export const previewQuickBooksTransactions = async ({
  organizationId,
  startDate,
  endDate
}) => {
  const orgId = validateOrganizationId(
    organizationId
  );

  const range = validateDateRange(
    startDate,
    endDate
  );

  const result = await invokeQuickBooksFunction(
    'quickbooks-import',
    {
      action: 'preview',
      organizationId: orgId,
      ...range
    }
  );

  if (
    result.action !== 'preview' ||
    !Array.isArray(result.transactions)
  ) {
    console.error(
      '[QuickBooks] Unexpected preview response:',
      {
        action: result.action,
        responseKeys: Object.keys(result),
        hasTransactions: Array.isArray(result.transactions)
      }
    );

    throw new Error(
      'The deployed quickbooks-import Edge Function does not ' +
      'return the expected transaction preview format. ' +
      'Verify that the latest transaction-first Edge Function ' +
      'is deployed in Supabase.'
    );
  }

  return {
    ...result,
    transactions: result.transactions
  };
};

/*
 * =========================================================
 * STAGE QUICKBOOKS TRANSACTIONS
 * =========================================================
 *
 * Calls quickbooks-import with action: stage
 *
 * Does not post transactions to the live ledger.
 * =========================================================
 */

export const stageQuickBooksTransactions = async ({
  organizationId,
  startDate,
  endDate,
  selectedPostingKeys,
  selectedKeys
}) => {
  const orgId = validateOrganizationId(
    organizationId
  );

  const range = validateDateRange(
    startDate,
    endDate
  );

  const keys = selectedPostingKeys ?? selectedKeys;

  if (!Array.isArray(keys) || keys.length === 0) {
    throw new Error(
      'Select at least one QuickBooks transaction.'
    );
  }

  const normalizedKeys = [
    ...new Set(
      keys.map((key) => String(key ?? '').trim())
    )
  ];

  if (
    normalizedKeys.some((key) => !key) ||
    normalizedKeys.length !== keys.length
  ) {
    throw new Error(
      'Transaction selection contains invalid or duplicate keys.'
    );
  }

  const result = await invokeQuickBooksFunction(
    'quickbooks-import',
    {
      action: 'stage',
      organizationId: orgId,
      ...range,
      selectedKeys: normalizedKeys
    }
  );

  return {
    ...result,
    batchId:
      result.batchId ??
      result.importBatchId ??
      null,
    stagedCount:
      result.stagedCount ??
      result.importedCount ??
      result.statistics?.transactionsStaged ??
      0
  };
};

/*
 * =========================================================
 * GET QUICKBOOKS CHART OF ACCOUNTS
 * =========================================================
 *
 * Used only for reference and account mapping.
 * Does not import or create ProfitYo accounts.
 * =========================================================
 */

export const getQuickBooksAccounts = async (
  organizationId
) => {
  const orgId = validateOrganizationId(
    organizationId
  );

  const result = await invokeQuickBooksFunction(
    'quickbooks-accounts',
    {
      organizationId: orgId
    }
  );

  if (!Array.isArray(result.accounts)) {
    throw new Error(
      'QuickBooks returned an invalid account list.'
    );
  }

  const accounts = result.accounts
    .filter((account) =>
      account?.id != null ||
      account?.Id != null
    )
    .map((account) => ({
      ...account,

      id: String(account.id ?? account.Id),

      name: String(
        account.name ??
        account.Name ??
        account.FullyQualifiedName ??
        ''
      ),

      fullyQualifiedName:
        account.fullyQualifiedName ??
        account.FullyQualifiedName ??
        null,

      type:
        account.type ??
        account.AccountType ??
        account.Classification ??
        '',

      classification:
        account.classification ??
        account.Classification ??
        '',

      subType:
        account.subType ??
        account.AccountSubType ??
        '',

      code:
        account.code ??
        account.AcctNum ??
        null,

      currency:
        account.currency ??
        account.CurrencyRef?.value ??
        null
    }));

  return {
    ...result,
    accounts
  };
};

/*
 * =========================================================
 * GET EXISTING PROFITYO ACCOUNTS
 * =========================================================
 *
 * Read-only.
 * Scoped to the selected client organization.
 * Uses Supabase accounting schema and RLS.
 * =========================================================
 */

export const getProfitYoAccountsForQuickBooks = async (
  organizationId
) => {
  const orgId = validateOrganizationId(
    organizationId
  );

  await requireSession();

  const allAccounts = [];
  let offset = 0;

  while (true) {
    const { data, error } = await supabase
      .schema('accounting')
      .from('accounts')
      .select(`
        id,
        organization_id,
        code,
        name,
        type,
        sub_type,
        currency,
        is_active
      `)
      .eq('organization_id', orgId)
      .order('name', { ascending: true })
      .order('id', { ascending: true })
      .range(
        offset,
        offset + ACCOUNT_PAGE_SIZE - 1
      );

    if (error) {
      console.error(
        '[QuickBooks] ProfitYo accounts lookup failed:',
        error
      );

      throw new Error(
        error.message ||
        'Unable to load ProfitYo accounts.'
      );
    }

    const page = data || [];

    allAccounts.push(
      ...page.map((account) => ({
        ...account,
        subType: account.sub_type
      }))
    );

    if (page.length < ACCOUNT_PAGE_SIZE) {
      break;
    }

    offset += ACCOUNT_PAGE_SIZE;
  }

  return allAccounts;
};

/*
 * =========================================================
 * OPTIONAL ALIASES
 * =========================================================
 *
 * Support different import component naming conventions.
 * =========================================================
 */

export const getQuickBooksTransactionsPreview =
  previewQuickBooksTransactions;

export const importQuickBooksTransactions =
  stageQuickBooksTransactions;